import { Button, Modal } from '@evaneos/b2b-ui/legacy';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import { useNavigate } from 'react-router-dom';

import { SidePanelViewMode } from '../../../../SidePanel/types';
import { changeCurrencyPaymentSchedule } from '../../../../../../resources/contract/actions';
import {
    calculateTotalAmountOfInstalment,
    paymentScheduleValidationSchema,
} from '../../../../../../resources/contract/operations';
import {
    getContractPaymentSchedule,
    hasPaidOrWaitingForApprovalInstalments as hasPaidOrWaitingForApprovalInstalmentsSelector,
    shouldDisplayChangeCurrencyModal as shouldDisplayChangeCurrencyModalSelector,
} from '../../../../../../resources/contract/selectors';
import { ContractEditStep, ContractId } from '../../../../../../resources/contract/types';
import { useAppDispatch, useAppSelector } from '../../../../../../shared/redux';
import styleContract from '../../../Contract.module.css';
import { SelectCurrency } from '../SelectCurrency';
import { StepButtons } from '../StepButtons';

import { useGetDossierData } from '../../../../../../resources/dossier/queries';

import { FilledInstalmentList } from './FilledInstalmentList';
import s from './PaymentScheduleStep.module.css';

type Props = {
    contractId: ContractId;
    onComplete?: () => void;
    viewMode?: SidePanelViewMode;
};

export function FilledPaymentSchedule({
    contractId,
    onComplete,
    viewMode,
}: Readonly<Props>): JSX.Element {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const intl = useIntl();

    const { marketCode: dossierMarketCode } = useGetDossierData(contractId)?.dossier?.project || {};

    const initialPaymentSchedule = useAppSelector(getContractPaymentSchedule);
    const hasPaidOrWaitingForApprovalInstalments = useAppSelector(
        hasPaidOrWaitingForApprovalInstalmentsSelector,
    );
    const shouldDisplayChangeCurrencyModal = useAppSelector(
        shouldDisplayChangeCurrencyModalSelector,
    );

    const [isChangeCurrencyModalOpened, setIsChangeCurrencyModalOpened] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(
        initialPaymentSchedule.currency as string,
    );

    return (
        <div className={s.paymentSchedule} {...(viewMode && { 'data-contract-view': viewMode })}>
            <p className={styleContract.stepSubtitle}>
                <FormattedMessage
                    id="contract.step_subtitle"
                    values={{
                        step: ContractEditStep.PAYMENT_SCHEDULE,
                    }}
                />
            </p>
            <h1 className="title-m">
                <FormattedMessage id="contract.payment_schedule.title" />
            </h1>
            <Formik
                initialValues={initialPaymentSchedule}
                enableReinitialize
                validationSchema={paymentScheduleValidationSchema}
                onSubmit={(values) => {
                    if (values.currency === undefined) {
                        return;
                    }
                    if (onComplete) {
                        onComplete();
                    } else {
                        navigate('./');
                    }
                }}
            >
                {({ values, isValid }) => (
                    <Form className={s.paymentForm}>
                        <SelectCurrency
                            wrapperClassName={s.currency}
                            name="currency"
                            disabled={hasPaidOrWaitingForApprovalInstalments}
                            inputId="currency"
                            labelTranslationId="contract.payment_schedule.currency"
                            dossierMarketCode={dossierMarketCode}
                            onChange={
                                shouldDisplayChangeCurrencyModal
                                    ? (value: string) => {
                                          setIsChangeCurrencyModalOpened(true);
                                          setSelectedCurrency(value);
                                      }
                                    : undefined
                            }
                        />
                        <FilledInstalmentList
                            contractId={contractId}
                            instalments={values.depositInstalments}
                            name="depositInstalments"
                            type="deposit"
                        />
                        <FilledInstalmentList
                            contractId={contractId}
                            instalments={values.balanceInstalments}
                            name="balanceInstalments"
                            type="balance"
                        />
                        <div className={s.total}>
                            <p>
                                <FormattedMessage id="contract.payment_schedule.total_price" />
                            </p>
                            <p className="title-m" data-testid="all-instalments-total-amount">
                                <FormattedNumber
                                    value={
                                        calculateTotalAmountOfInstalment(
                                            values.depositInstalments,
                                        ) +
                                        calculateTotalAmountOfInstalment(values.balanceInstalments)
                                    }
                                    style="currency"
                                    currency={values.currency || 'EUR'}
                                />
                            </p>
                        </div>
                        <Modal
                            isOpen={isChangeCurrencyModalOpened}
                            title={intl.formatMessage({
                                id: 'contract.payment_schedule.currency_change.title',
                            })}
                            onRequestClose={() => setIsChangeCurrencyModalOpened(false)}
                            mainButton={
                                <Button
                                    danger
                                    onClick={() => {
                                        dispatch(
                                            changeCurrencyPaymentSchedule(values, selectedCurrency),
                                        );
                                        setIsChangeCurrencyModalOpened(false);
                                    }}
                                >
                                    <FormattedMessage id="contract.payment_schedule.currency_change.submit" />
                                </Button>
                            }
                            secondaryButton={
                                <Button
                                    priority="secondary"
                                    onClick={() => setIsChangeCurrencyModalOpened(false)}
                                >
                                    <FormattedMessage id="form.cancel" />
                                </Button>
                            }
                        >
                            <p>
                                <FormattedMessage
                                    id="contract.payment_schedule.currency_change.description"
                                    values={{
                                        currency: <strong>{values.currency}</strong>,
                                        newCurrency: <strong>{selectedCurrency}</strong>,
                                    }}
                                />
                            </p>
                        </Modal>
                        <StepButtons
                            isLoading={false}
                            isNextStepDisabled={!isValid}
                            dataTestId="button-submit-step-4"
                            isPaymentScheduleCreated={true}
                            viewMode={viewMode}
                        />
                    </Form>
                )}
            </Formik>
        </div>
    );
}
